import { useState, useCallback, lazy, Suspense, useEffect, useRef } from 'react'
import { Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useSSEConnected } from '../../hooks/useSSE'
import { usePersonaStatusMap } from '../../hooks/usePersonaStatusMap'
import { useAuth } from '../../context/AuthContext'
import { fetchPersonaState, spawnPersona } from '../../lib/api'
import PersonaAvatar from '../../components/PersonaAvatar'
import PersonaMetricsGrid from '../../components/PersonaMetricsGrid'
import type { PersonaState } from '../../lib/types'

const TerminalTile = lazy(() => import('../../components/TerminalTile'))

interface PersonaFull extends PersonaState {
  name: string
  queue_count?: number
  session_type?: string
  tmux_pane?: string
  category?: string
}

interface VoiceCreditsResponse {
  error?: string
  character_count?: number
  character_limit?: number
  remaining?: number
  remaining_pct?: number
  warn_level?: 'ok' | 'warning' | 'critical' | 'exhausted' | 'unknown'
  tier?: string | null
  next_reset?: string | null
  cached?: boolean
  last_checked?: string
}

function VoiceCreditsCard() {
  const { data } = useQuery<VoiceCreditsResponse>({
    queryKey: ['voiceCredits'],
    queryFn: async () => {
      const r = await fetch('/api/voice/credits')
      if (!r.ok) return { error: `HTTP ${r.status}`, warn_level: 'unknown' as const }
      return r.json()
    },
    refetchInterval: 60_000, // poll once a minute; server caches 60s regardless
    staleTime: 30_000,
  })
  if (!data) return null
  const level = data.warn_level || 'unknown'
  const colorByLevel: Record<string, string> = {
    ok: 'var(--success)',
    warning: '#d29922',
    critical: '#ef4444',
    exhausted: '#ef4444',
    unknown: 'var(--text-muted)',
  }
  const color = colorByLevel[level] || 'var(--text-muted)'
  const bgByLevel: Record<string, string> = {
    ok: 'rgba(63,185,80,0.08)',
    warning: 'rgba(210,153,34,0.1)',
    critical: 'rgba(239,68,68,0.12)',
    exhausted: 'rgba(239,68,68,0.18)',
    unknown: 'var(--bg-surface)',
  }
  return (
    <div className="mb-6 rounded-lg p-3" style={{
      background: bgByLevel[level] || 'var(--bg-surface)',
      border: `1px solid ${level === 'ok' ? 'var(--border)' : color}`,
    }}>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
          Voice Credits (ElevenLabs)
        </h2>
        <span className="text-xs px-2 py-0.5 rounded-full font-semibold uppercase" style={{ background: 'rgba(0,0,0,0.15)', color, fontSize: 10 }}>
          {level}
        </span>
      </div>
      {data.error ? (
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {data.error === 'ELEVENLABS_API_KEY not set on Hetzner (switch off)'
            ? 'Chatter TTS disabled — ELEVENLABS_API_KEY not set. No cost accruing. Set the env var to enable.'
            : `Credit check failed: ${data.error}`}
        </div>
      ) : (
        <>
          <div className="flex items-baseline gap-3 mb-1">
            <span className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
              {(data.remaining || 0).toLocaleString()}
            </span>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              chars remaining of {(data.character_limit || 0).toLocaleString()} ({data.remaining_pct}%)
            </span>
          </div>
          <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-body)' }}>
            <div style={{
              width: `${Math.max(0, Math.min(100, data.remaining_pct || 0))}%`,
              height: '100%',
              background: color,
              transition: 'width 0.3s',
            }} />
          </div>
          <div className="flex items-center justify-between mt-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
            <span>Tier: {data.tier || 'unknown'}</span>
            {data.next_reset && <span>Resets: {new Date(data.next_reset).toLocaleDateString()}</span>}
          </div>
        </>
      )}
    </div>
  )
}

function ContextBar({ pct }: { pct: number }) {
  const color = pct >= 75 ? 'var(--danger)' : pct >= 50 ? 'var(--warning)' : 'var(--accent)'
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-12 h-1 rounded-full overflow-hidden" style={{ background: 'var(--bg-elevated)' }}>
        <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
      </div>
      <span className="text-xs font-mono" style={{ color, fontSize: 10 }}>{pct}%</span>
    </div>
  )
}

export default function OperationsPage() {
  const { user } = useAuth()
  if (!user?.isOperator && user?.role !== 'admin') {
    return <Navigate to="/" replace />
  }
  const [selectedPersona, setSelectedPersona] = useState<string | null>(() => {
    return sessionStorage.getItem('ops-last-persona') || null
  })
  const [splitPersona, setSplitPersona] = useState<string | null>(null)
  const [splitRatio, setSplitRatio] = useState(0.5) // 0-1, left terminal's share
  const [sidebarOpen, _setSidebarOpen] = useState(() => {
    return localStorage.getItem('ops-sidebar') !== '0'
  })
  const setSidebarOpen = (val: boolean) => {
    _setSidebarOpen(val)
    localStorage.setItem('ops-sidebar', val ? '1' : '0')
  }
  const [acting, setActing] = useState<Record<string, boolean | string>>({})
  const termAreaRef = useRef<HTMLDivElement>(null)
  const [terminals, setTerminals] = useState<string[]>([])
  const [maximizedTerminal, setMaximizedTerminal] = useState<number | null>(null)
  const [dashboardCollapsed, setDashboardCollapsed] = useState(false)
  const [opsTab, setOpsTab] = useState<'status' | 'metrics' | 'wallets'>('status')
  const termPanelRef = useRef<HTMLDivElement>(null)

  // Persist last-viewed persona (per-tab via sessionStorage)
  useEffect(() => {
    if (selectedPersona) {
      sessionStorage.setItem('ops-last-persona', selectedPersona)
    }
  }, [selectedPersona])

  // Handle persona selection: regular click = switch, shift+click = split
  const handleSelectPersona = useCallback((name: string, shiftKey: boolean) => {
    if (shiftKey && selectedPersona && name !== selectedPersona) {
      setSplitPersona(name)
      // Trigger re-fit after layout settles
      setTimeout(() => window.dispatchEvent(new Event('resize')), 200)
    } else {
      setSelectedPersona(name)
      setSplitPersona(null)
      setTimeout(() => window.dispatchEvent(new Event('resize')), 200)
    }
  }, [selectedPersona])

  const sseOk = useSSEConnected()

  const { data: state } = useQuery({
    queryKey: ['personaState'],
    queryFn: fetchPersonaState,
    refetchInterval: sseOk ? false : 30_000,
    staleTime: 25_000,
    structuralSharing: true,
  })

  const statusMap = usePersonaStatusMap()

  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: async () => {
      const res = await fetch('/api/health')
      if (!res.ok) return null
      return res.json()
    },
    refetchInterval: sseOk ? false : 60_000, // SSE-conditional polling fallback
  })

  const personas = state
    ? Object.entries(state).map(([name, s]) => ({ ...s, name } as PersonaFull)).sort((a, b) => {
        if (a.awake && !b.awake) return -1
        if (!a.awake && b.awake) return 1
        return a.name.localeCompare(b.name)
      })
    : []

  const awake = personas.filter(p => p.awake)
  const sleeping = personas.filter(p => !p.awake)
  const totalQueue = personas.reduce((sum, p) => sum + (p.queue_count || 0), 0)

  // Detect if on localhost/Tailscale — terminal only available locally
  const isLocal = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('100.')
  )
  const [view, setView] = useState<'dashboard' | 'terminal'>(isLocal ? 'terminal' : 'dashboard')

  // Ticket system state
  const [ticketTitle, setTicketTitle] = useState('')
  const [ticketDesc, setTicketDesc] = useState('')
  const [ticketAssign, setTicketAssign] = useState('')
  const [ticketScreenshot, setTicketScreenshot] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  const authToken = (localStorage.getItem('session_token') || document.cookie.match(/session=([^;]+)/)?.[1])
  const ticketAuthHeaders: Record<string, string> = authToken ? { Authorization: `Bearer ${authToken}` } : {}

  const { data: tickets, refetch: refetchTickets } = useQuery<{ id: string; title: string; description: string; status: string; submitted_by: string; assigned_to: string | null; created_at: string; screenshot_url: string | null }[]>({
    queryKey: ['ops-tickets'],
    queryFn: async () => {
      const res = await fetch('/api/tickets', { headers: ticketAuthHeaders })
      if (!res.ok) return []
      return res.json()
    },
    staleTime: 15_000,
  })

  async function handleScreenshotUpload(file: File) {
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch('/api/tickets/upload', { method: 'POST', headers: ticketAuthHeaders, body: formData })
      const data = await res.json()
      if (data.url) setTicketScreenshot(data.url)
    } catch { /* silent */ }
    setUploading(false)
  }

  async function submitTicket() {
    if (!ticketTitle.trim()) return
    await fetch('/api/tickets', {
      method: 'POST',
      headers: { ...ticketAuthHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: ticketTitle.trim(),
        description: ticketDesc.trim() || null,
        assigned_to: ticketAssign || null,
        submitted_by: 'community',
        screenshot_url: ticketScreenshot,
      }),
    })
    setTicketTitle('')
    setTicketDesc('')
    setTicketAssign('')
    setTicketScreenshot(null)
    refetchTickets()
  }

  // Dashboard view for community users (and default for everyone)
  if (view === 'dashboard') {
    return (
      <div className="flex h-full overflow-hidden">
      {/* Collapsed dashboard tab */}
      {(dashboardCollapsed || maximizedTerminal != null) && terminals.length > 0 && (
        <div
          onClick={() => { setDashboardCollapsed(false); setMaximizedTerminal(null) }}
          className="shrink-0 flex items-center justify-center cursor-pointer transition-colors hover:bg-[var(--bg-elevated)]"
          style={{
            width: 28,
            borderRight: '1px solid var(--border)',
            background: 'var(--bg-body)',
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
          }}
          title="Show dashboard"
        >
          <span className="text-xs font-medium" style={{ color: 'var(--text-muted)', letterSpacing: 2 }}>
            ⚙ OPS
          </span>
        </div>
      )}
      <div className="overflow-y-auto px-6 py-4 flex-1 min-w-0 transition-all" style={{ maxWidth: terminals.length > 0 ? undefined : 960, margin: terminals.length > 0 ? undefined : '0 auto', display: (dashboardCollapsed || maximizedTerminal != null) && terminals.length > 0 ? 'none' : undefined }}>
        {/* Header */}
        <div className="flex items-center gap-4 mb-5">
          <div>
            <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Operations</h1>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              System health, community status, and issue tracking
            </p>
          </div>
          <div className="flex-1" />
          {terminals.length > 0 && (
            <button
              onClick={() => { setDashboardCollapsed(true); setTimeout(() => window.dispatchEvent(new Event('resize')), 200) }}
              className="text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors hover:brightness-110"
              style={{
                background: 'rgba(148,163,184,0.12)',
                color: '#94a3b8',
                border: '1px solid rgba(148,163,184,0.25)',
                cursor: 'pointer',
              }}
            >
              ◀ Collapse
            </button>
          )}
          {isLocal && (
            <button
              onClick={() => {
                setView('terminal')
                if (!selectedPersona) {
                  const firstAwake = personas.find(p => p.awake)
                  if (firstAwake) setSelectedPersona(firstAwake.name)
                }
              }}
              className="text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors hover:brightness-110"
              style={{
                background: 'rgba(96,165,250,0.12)',
                color: '#60a5fa',
                border: '1px solid rgba(96,165,250,0.25)',
                cursor: 'pointer',
              }}
            >
              🖥 Terminal
            </button>
          )}
        </div>

        {/* Server Health Stats */}
        <div className="mb-6">
          <h2 className="text-xs font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>
            System Health
          </h2>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
            <div className="rounded-lg p-3" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Personas</div>
              <div className="text-xl font-semibold mt-1" style={{ color: 'var(--text-primary)' }}>
                {awake.length} <span className="text-xs font-normal" style={{ color: 'var(--text-muted)' }}>/ {personas.length}</span>
              </div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--success)' }}>awake</div>
            </div>
            <div className="rounded-lg p-3" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Queue</div>
              <div className="text-xl font-semibold mt-1" style={{ color: totalQueue > 0 ? 'var(--warning)' : 'var(--text-primary)' }}>
                {totalQueue}
              </div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>messages</div>
            </div>
            {health && health.memory && (
              <div className="rounded-lg p-3" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Memory</div>
                <div className="text-xl font-semibold mt-1" style={{ color: 'var(--text-primary)' }}>
                  {health.memory.heap_used_mb || Math.round((health.memory.heapUsed || 0) / 1024 / 1024)}
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>MB heap</div>
              </div>
            )}
            {health && health.uptime_seconds && (
              <div className="rounded-lg p-3" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Uptime</div>
                <div className="text-xl font-semibold mt-1" style={{ color: 'var(--text-primary)' }}>
                  {Math.floor(health.uptime_seconds / 3600)}h
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{Math.floor((health.uptime_seconds % 3600) / 60)}m</div>
              </div>
            )}
            {health && health.connections != null && (
              <div className="rounded-lg p-3" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Connections</div>
                <div className="text-xl font-semibold mt-1" style={{ color: 'var(--text-primary)' }}>
                  {typeof health.connections === 'object'
                    ? (health.connections.websocket || 0) + (health.connections.sse || 0)
                    : health.connections}
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>active</div>
              </div>
            )}
          </div>
        </div>

        {/* Voice Credits — task-010 of Conversation Voice (Apr 13 2026).
            Polls /api/voice/credits which caches ElevenLabs quota for 60s.
            Shows remaining budget + warn level so Joel can see spend trajectory
            in near-real-time. This is what makes the ELEVENLABS_API_KEY flip safe. */}
        <VoiceCreditsCard />

        {/* Tab toggle */}
        <div className="flex gap-2 mb-4">
          {(['status', 'metrics', 'wallets'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setOpsTab(tab)}
              className="text-xs px-3 py-1.5 rounded-md font-medium transition-colors"
              style={{
                background: opsTab === tab ? 'var(--bg-elevated)' : 'transparent',
                color: opsTab === tab ? 'var(--text-primary)' : 'var(--text-muted)',
                border: `1px solid ${opsTab === tab ? 'var(--border-strong, #3a3a44)' : 'var(--border)'}`,
                cursor: 'pointer',
              }}
            >
              {tab === 'status' ? 'Persona Status' : tab === 'metrics' ? 'Growth Metrics' : 'Wallets'}
            </button>
          ))}
        </div>

        {/* Metrics View */}
        {opsTab === 'metrics' && (
          <div className="mb-6">
            <PersonaMetricsGrid />
          </div>
        )}

        {/* Wallets View */}
        {opsTab === 'wallets' && <WalletsTab />}

        {/* Persona Status */}
        {opsTab === 'status' && <div className="mb-6">
          <h2 className="text-xs font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>
            Persona Status
          </h2>
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
            {awake.map(p => (
              <div
                key={p.name}
                onClick={() => {
                  const idx = terminals.indexOf(p.name)
                  if (idx >= 0) {
                    // Close this terminal
                    setTerminals(prev => prev.filter(t => t !== p.name))
                    setMaximizedTerminal(null)
                  } else {
                    // Add terminal
                    setTerminals(prev => [...prev, p.name])
                    setMaximizedTerminal(null)
                  }
                  setTimeout(() => window.dispatchEvent(new Event('resize')), 200)
                }}
                className="rounded-lg p-2.5 flex items-center gap-2 cursor-pointer transition-all"
                style={{
                  background: terminals.includes(p.name) ? 'var(--bg-elevated)' : 'var(--bg-surface)',
                  border: terminals.includes(p.name) ? '1px solid var(--accent)' : '1px solid var(--border)',
                }}
              >
                {(() => {
                  const entry = statusMap[p.name.toLowerCase()]
                  const level = entry?.level ?? 'active'
                  const queueCount = entry?.queueCount ?? 0
                  const taskCount = entry?.taskCount ?? 0
                  const title = `${p.name} — ${level}${queueCount > 0 ? ` · ${queueCount} queued` : ''}${taskCount > 0 ? ` · ${taskCount} tasks` : ''}`
                  return (
                    <>
                      <PersonaAvatar name={p.name} size={24} showStatus status={level} queueCount={queueCount} title={title} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate flex items-center gap-1.5" style={{ color: 'var(--text-primary)' }}>
                          {p.name}
                          {p.session_type && (
                            <span className="px-1 rounded" style={{
                              background: p.session_type === 'tmux' ? 'rgba(96,165,250,0.15)' : 'rgba(192,132,252,0.15)',
                              color: p.session_type === 'tmux' ? '#60a5fa' : '#c084fc',
                              fontSize: 9,
                            }}>
                              {p.session_type === 'tmux' ? 'tmux' : 'iTerm'}
                            </span>
                          )}
                          {level === 'idle' && (
                            <span className="px-1 rounded" style={{ background: 'rgba(210,153,34,0.15)', color: '#d29922', fontSize: 9, fontWeight: 600 }}>IDLE</span>
                          )}
                          {taskCount > 0 && (
                            <span className="px-1 rounded" style={{ background: 'rgba(88,166,255,0.15)', color: '#58a6ff', fontSize: 9 }} title={`${taskCount} assigned tasks`}>
                              {taskCount}T
                            </span>
                          )}
                        </div>
                        {p.status_line && (
                          <div className="text-xs truncate" style={{ color: 'var(--text-muted)', fontSize: 10 }}>
                            {p.status_line}
                          </div>
                        )}
                      </div>
                      {p.context_pct != null && <ContextBar pct={p.context_pct} />}
                      <div className="flex gap-0.5 shrink-0" onClick={e => e.stopPropagation()}>
                        {acting[p.name] === 'sleeping' ? (
                          <span className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(210,153,34,0.2)', color: '#d29922', fontSize: 9, fontWeight: 600 }}>
                            sleeping…
                          </span>
                        ) : acting[p.name] === 'killing' ? (
                          <span className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(239,68,68,0.2)', color: '#ef4444', fontSize: 9, fontWeight: 600 }}>
                            killing…
                          </span>
                        ) : (
                        <>
                        <button
                          onClick={() => handleForceNap(p.name)}
                          disabled={!!acting[p.name]}
                          className="text-xs px-1.5 py-1 rounded transition-opacity"
                          style={{ background: 'rgba(210,153,34,0.1)', color: '#d29922', border: '1px solid rgba(210,153,34,0.2)', cursor: acting[p.name] ? 'wait' : 'pointer', opacity: acting[p.name] ? 0.3 : 0.7, fontSize: 10 }}
                          title={`Send ${p.name} to sleep`}
                        >
                          💤
                        </button>
                        <button
                          onClick={() => handleKill(p.name)}
                          disabled={!!acting[p.name]}
                          className="text-xs px-1.5 py-1 rounded transition-opacity"
                          style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)', cursor: acting[p.name] ? 'wait' : 'pointer', opacity: acting[p.name] ? 0.3 : 0.7, fontSize: 10 }}
                          title={`Kill ${p.name}`}
                        >
                          ✕
                        </button>
                        </>
                        )}
                      </div>
                    </>
                  )
                })()}
              </div>
            ))}
          </div>
          {sleeping.length > 0 && (
            <>
              <h2 className="text-xs font-medium mt-4 mb-3" style={{ color: 'var(--text-secondary)' }}>
                Sleeping ({sleeping.length})
              </h2>
              <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                {sleeping.map(p => (
                  <div
                    key={p.name}
                    className="rounded-lg p-2.5 flex items-center gap-2"
                    style={{
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border)',
                      opacity: 0.6,
                    }}
                  >
                    {(() => {
                      const entry = statusMap[p.name.toLowerCase()]
                      const queueCount = entry?.queueCount ?? 0
                      const title = `${p.name} — sleeping${queueCount > 0 ? ` · ${queueCount} queued (will wake)` : ''}`
                      return (
                        <>
                          <PersonaAvatar name={p.name} size={24} showStatus status="sleeping" queueCount={queueCount} title={title} />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate flex items-center gap-1.5" style={{ color: 'var(--text-primary)' }}>
                              {p.name}
                              {queueCount > 0 && (
                                <span className="px-1 rounded" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', fontSize: 9, fontWeight: 600 }} title="queued messages will wake this persona">
                                  {queueCount}Q
                                </span>
                              )}
                            </div>
                          </div>
                        </>
                      )
                    })()}
                    <button
                      onClick={async (e) => {
                        e.stopPropagation()
                        if (acting[p.name]) return
                        setActing(prev => ({ ...prev, [p.name]: true }))
                        try {
                          await spawnPersona(p.name)
                        } catch { /* spawn may take time */ }
                        setTimeout(() => setActing(prev => ({ ...prev, [p.name]: false })), 5000)
                      }}
                      disabled={!!acting[p.name]}
                      className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all"
                      style={{
                        background: acting[p.name] ? 'var(--success)' : 'rgba(74,222,128,0.15)',
                        border: '1px solid rgba(74,222,128,0.3)',
                        cursor: acting[p.name] ? 'wait' : 'pointer',
                        opacity: acting[p.name] ? 0.5 : 1,
                      }}
                      title={`Wake ${p.name}`}
                    >
                      <span style={{ color: 'var(--success)', fontSize: 10, lineHeight: 1 }}>⏻</span>
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>}

        {/* Ticket System */}
        <div>
          <h2 className="text-xs font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>
            Issue Tracker
          </h2>

          {/* Submit form */}
          <div
            className="rounded-lg p-4 mb-3"
            style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
          >
            <input
              value={ticketTitle}
              onChange={(e) => setTicketTitle(e.target.value)}
              placeholder="What's the issue?"
              className="w-full text-sm px-3 py-2 rounded-md outline-none mb-2"
              style={{
                background: 'var(--bg-elevated)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)',
              }}
            />
            <textarea
              value={ticketDesc}
              onChange={(e) => setTicketDesc(e.target.value)}
              placeholder="Details (optional)"
              rows={2}
              className="w-full text-xs px-3 py-2 rounded-md outline-none resize-none mb-2"
              style={{
                background: 'var(--bg-elevated)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)',
              }}
            />
            <div className="flex gap-2 items-center">
              <select
                value={ticketAssign}
                onChange={(e) => setTicketAssign(e.target.value)}
                className="text-xs px-2.5 py-1.5 rounded-md outline-none"
                style={{
                  background: 'var(--bg-elevated)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border)',
                }}
              >
                <option value="">Assign to... (optional)</option>
                {personas.filter(p => p.awake).map(p => (
                  <option key={p.name} value={p.name}>{p.name}</option>
                ))}
              </select>
              <label
                className="text-xs px-2.5 py-1.5 rounded-md cursor-pointer"
                style={{ background: 'var(--bg-elevated)', color: ticketScreenshot ? 'var(--success)' : 'var(--text-muted)', border: '1px solid var(--border)' }}
              >
                {uploading ? 'Uploading...' : ticketScreenshot ? '📎 Attached' : '📷 Screenshot'}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={e => { if (e.target.files?.[0]) handleScreenshotUpload(e.target.files[0]) }}
                />
              </label>
              <div className="flex-1" />
              <button
                onClick={submitTicket}
                disabled={!ticketTitle.trim()}
                className="text-xs px-4 py-1.5 rounded-md font-medium"
                style={{
                  background: ticketTitle.trim() ? 'var(--accent)' : 'var(--bg-elevated)',
                  color: ticketTitle.trim() ? '#fff' : 'var(--text-muted)',
                  border: 'none',
                  cursor: ticketTitle.trim() ? 'pointer' : 'default',
                }}
              >
                Submit
              </button>
            </div>
            {ticketScreenshot && (
              <div className="mt-2 flex items-center gap-2">
                <img src={ticketScreenshot} alt="Screenshot" style={{ maxHeight: 60, borderRadius: 4, border: '1px solid var(--border)' }} />
                <button onClick={() => setTicketScreenshot(null)} className="text-xs" style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>Remove</button>
              </div>
            )}
          </div>

          {/* Ticket list */}
          {(tickets || []).length > 0 ? (
            <div className="flex flex-col gap-2">
              {(tickets || []).map(t => (
                <div
                  key={t.id}
                  className="rounded-lg p-3 flex items-start gap-3"
                  style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
                >
                  <span
                    className="text-xs px-1.5 py-0.5 rounded-full shrink-0 mt-0.5"
                    style={{
                      background: t.status === 'resolved' ? 'rgba(52,211,153,0.15)' : t.status === 'in-progress' ? 'rgba(251,191,36,0.15)' : 'rgba(148,163,184,0.15)',
                      color: t.status === 'resolved' ? '#34d399' : t.status === 'in-progress' ? '#fbbf24' : '#94a3b8',
                      fontSize: 10,
                    }}
                  >
                    {t.status}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {t.title}
                      </div>
                      {t.assigned_to && (
                        <div className="flex items-center gap-1">
                          <PersonaAvatar name={t.assigned_to} size={14} />
                          <span className="text-xs" style={{ color: 'var(--text-muted)', fontSize: 10 }}>
                            {t.assigned_to}
                          </span>
                        </div>
                      )}
                    </div>
                    {t.description && (
                      <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                        {t.description}
                      </div>
                    )}
                    {t.screenshot_url && (
                      <a href={t.screenshot_url} target="_blank" rel="noopener noreferrer">
                        <img src={t.screenshot_url} alt="Screenshot" style={{ maxHeight: 80, borderRadius: 4, border: '1px solid var(--border)', marginTop: 6, cursor: 'pointer' }} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
              <p className="text-xs">No issues reported. When something breaks, submit it here.</p>
            </div>
          )}
        </div>
      </div>

      {/* ─── Terminal Panel(s) ──── */}
      {terminals.length > 0 && (
        <div
          ref={termPanelRef}
          className="shrink-0 flex overflow-hidden"
          style={{
            width: (dashboardCollapsed || maximizedTerminal != null) ? '100%' : (terminals.length >= 2 ? '65%' : '50%'),
            minWidth: 400,
            borderLeft: '1px solid var(--border)',
            background: '#0d1117',
            flex: (dashboardCollapsed || maximizedTerminal != null) ? '1 1 auto' : undefined,
          }}
        >
          {terminals.map((name, idx) => {
            // If one terminal is maximized, only show that one
            if (maximizedTerminal != null && maximizedTerminal !== idx) return null
            const p = personas.find(pp => pp.name === name)
            return (
              <div key={name} className="flex flex-col overflow-hidden min-w-0" style={{ flex: '1 1 0', borderLeft: idx > 0 ? '1px solid var(--border)' : undefined }}>
                <div className="flex items-center gap-1 px-2 py-1 shrink-0" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
                  <PersonaAvatar name={name} size={14} />
                  <span className="font-medium shrink-0" style={{ color: 'var(--text-primary)', fontSize: 11, whiteSpace: 'nowrap' }}>
                    {name[0].toUpperCase() + name.slice(1)}
                  </span>
                  <button
                    onClick={() => { setMaximizedTerminal(maximizedTerminal === idx ? null : idx); setDashboardCollapsed(maximizedTerminal !== idx); setTimeout(() => window.dispatchEvent(new Event('resize')), 200) }}
                    style={{ background: 'none', color: 'var(--text-muted)', border: 'none', cursor: 'pointer', fontSize: 11, padding: '0 2px' }}
                    title={maximizedTerminal === idx ? 'Restore' : 'Maximize'}
                  >
                    {maximizedTerminal === idx ? '⊡' : '⊞'}
                  </button>
                  <button
                    onClick={() => { setTerminals(prev => prev.filter(t => t !== name)); setMaximizedTerminal(null) }}
                    style={{ background: 'none', color: 'var(--text-muted)', border: 'none', cursor: 'pointer', fontSize: 11, padding: '0 2px' }}
                  >
                    ✕
                  </button>
                </div>
                <div className="flex-1 overflow-hidden">
                  <Suspense fallback={<div className="p-4 text-xs" style={{ color: 'var(--text-muted)' }}>Loading terminal...</div>}>
                    <TerminalTile
                      key={name}
                      persona={name}
                      tmuxPane={p?.tmux_pane}
                      awake={p?.awake}
                      hideHeader
                    />
                  </Suspense>
                </div>
              </div>
            )
          })}
        </div>
      )}
      </div>
    )
  }

  const handleForceNap = useCallback(async (name: string) => {
    setActing(prev => ({ ...prev, [name]: 'sleeping' }))
    try {
      const r = await fetch('/api/personas/nap', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('session_token') || ''}` },
        credentials: 'include',
        body: JSON.stringify({ persona: name }),
      })
      if (!r.ok) console.error(`Nap ${name} failed: ${r.status}`)
    } catch (e) { console.error(e) }
    setTimeout(() => setActing(prev => ({ ...prev, [name]: false })), 30000)
  }, [])

  const handleKill = useCallback(async (name: string) => {
    if (!confirm(`Kill ${name}? This skips the closing ritual.`)) return
    setActing(prev => ({ ...prev, [name]: 'killing' }))
    try {
      const r = await fetch('/api/personas/kill', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('session_token') || ''}` },
        credentials: 'include',
        body: JSON.stringify({ persona: name }),
      })
      if (!r.ok) console.error(`Kill ${name} failed: ${r.status}`)
    } catch (e) { console.error(e) }
    setTimeout(() => setActing(prev => ({ ...prev, [name]: false })), 10000)
  }, [])

  const handleSpawn = useCallback(async (name: string) => {
    setActing(prev => ({ ...prev, [name]: true }))
    try { await spawnPersona(name) } catch (e) { console.error(e) }
    setActing(prev => ({ ...prev, [name]: false }))
  }, [])

  function PersonaRow({ p }: { p: PersonaFull }) {
    const isSelected = selectedPersona === p.name
    const isSplit = splitPersona === p.name
    const isActing = !!acting[p.name]
    return (
      <div
        onClick={(e) => handleSelectPersona(p.name, e.shiftKey)}
        className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors"
        style={{
          background: (isSelected || isSplit) ? 'var(--bg-elevated)' : 'transparent',
          borderLeft: isSelected ? `2px solid var(--accent)` : isSplit ? '2px solid var(--warning)' : '2px solid transparent',
        }}
      >
        <PersonaAvatar name={p.name} size={20} showStatus awake={p.awake} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium truncate" style={{ color: isSelected ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
              {p.name}
            </span>
            {p.awake && p.session_type && (
              <span className="text-xs" style={{
                color: p.session_type === 'tmux' ? '#60a5fa' : '#c084fc',
                fontSize: 9,
              }}>
                {p.session_type === 'tmux' ? 'tmux' : 'iTrm'}
              </span>
            )}
          </div>
          {p.awake && p.context_pct != null && (
            <ContextBar pct={p.context_pct} />
          )}
        </div>

        {/* Queue badge */}
        {(p.queue_count || 0) > 0 && (
          <span className="text-xs px-1 py-0.5 rounded-full shrink-0" style={{ background: 'rgba(210,153,34,0.15)', color: 'var(--warning)', fontSize: 9 }}>
            {p.queue_count}
          </span>
        )}

        {/* Compact action buttons */}
        <div className="flex gap-0.5 shrink-0" onClick={e => e.stopPropagation()}>
          {p.awake ? (
            <>
              <button
                onClick={() => handleForceNap(p.name)}
                disabled={isActing}
                className="text-xs px-1 py-0.5 rounded"
                style={{ background: 'none', color: 'var(--warning)', border: 'none', cursor: 'pointer', opacity: isActing ? 0.3 : 0.6, fontSize: 11 }}
                title="Force nap"
              >
                💤
              </button>
              <button
                onClick={() => handleKill(p.name)}
                disabled={isActing}
                className="text-xs px-1 py-0.5 rounded"
                style={{ background: 'none', color: 'var(--danger)', border: 'none', cursor: 'pointer', opacity: isActing ? 0.3 : 0.6, fontSize: 11 }}
                title="Kill"
              >
                ✕
              </button>
            </>
          ) : (
            <button
              onClick={() => handleSpawn(p.name)}
              disabled={isActing}
              className="text-xs px-1 py-0.5 rounded"
              style={{ background: 'none', color: 'var(--text-muted)', border: 'none', cursor: 'pointer', opacity: isActing ? 0.3 : 0.7, fontSize: 11 }}
              title="Wake"
            >
              ⏻
            </button>
          )}
        </div>
      </div>
    )
  }

  // Terminal view — Joel-only, localhost
  return (
    <div className="flex h-full overflow-hidden">
      {/* ─── Left: Compact persona sidebar ──────────────── */}
      {sidebarOpen && (
        <div
          className="shrink-0 flex flex-col overflow-hidden"
          style={{ width: 220, borderRight: '1px solid var(--border)', background: 'var(--bg-body)' }}
        >
          {/* Sidebar header with stats */}
          <div className="shrink-0 px-2 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setView('dashboard')}
                  className="text-xs"
                  style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer' }}
                >
                  ← Dashboard
                </button>
                <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Terminal</span>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="text-xs px-1 rounded"
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                title="Collapse sidebar"
              >
                ◀
              </button>
            </div>
            <div className="flex items-center gap-3 text-xs" style={{ fontSize: 10 }}>
              <span style={{ color: 'var(--success)' }}>⬤ {awake.length} awake</span>
              <span style={{ color: 'var(--text-muted)' }}>{sleeping.length} sleep</span>
              {totalQueue > 0 && (
                <span style={{ color: 'var(--warning)' }}>Q:{totalQueue}</span>
              )}
            </div>
            {health && (
              <div className="text-xs mt-1" style={{ color: 'var(--text-muted)', fontSize: 9 }}>
                {health.memory && (
                  <span>{health.memory.heap_used_mb || Math.round((health.memory.heapUsed || 0) / 1024 / 1024)}MB heap</span>
                )}
                {health.connections != null && (
                  <span>
                    {' · '}
                    {typeof health.connections === 'object'
                      ? `${health.connections.websocket || 0}WS ${health.connections.sse || 0}SSE`
                      : `${health.connections}conn`}
                  </span>
                )}
                {health.uptime_seconds && (
                  <span> · {Math.floor(health.uptime_seconds / 3600)}h up</span>
                )}
              </div>
            )}
          </div>

          {/* Persona list */}
          <div className="flex-1 overflow-y-auto px-1 py-1">
            {/* Awake */}
            {awake.length > 0 && (
              <div className="mb-2">
                {awake.map(p => <PersonaRow key={p.name} p={p} />)}
              </div>
            )}

            {/* Hint */}
            {selectedPersona && !splitPersona && (
              <div className="text-xs px-2 py-1" style={{ color: 'var(--text-muted)', fontSize: 8, opacity: 0.5 }}>
                ⇧+click for split view
              </div>
            )}

            {/* Sleeping divider */}
            {sleeping.length > 0 && (
              <>
                <div className="text-xs px-2 py-1" style={{ color: 'var(--text-muted)', fontSize: 9 }}>
                  SLEEPING ({sleeping.length})
                </div>
                {sleeping.map(p => <PersonaRow key={p.name} p={p} />)}
              </>
            )}
          </div>

          {/* Stats are in the header */}
        </div>
      )}

      {/* Collapsed sidebar toggle */}
      {!sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(true)}
          className="shrink-0 flex items-center justify-center cursor-pointer hover:bg-[var(--bg-elevated)] transition-colors"
          style={{ width: 24, borderRight: '1px solid var(--border)', background: 'var(--bg-body)' }}
          title="Expand sidebar"
        >
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>▶</span>
        </div>
      )}

      {/* ─── Right: Terminal area (fills remaining space) ──── */}
      <div ref={termAreaRef} className="flex-1 flex overflow-hidden" style={{ background: '#0d1117' }}>
        {selectedPersona ? (
          <>
            {/* Primary terminal */}
            <div className="flex flex-col overflow-hidden min-w-0" style={{ width: splitPersona ? `${splitRatio * 100}%` : '100%' }}>
              <div className="flex items-center gap-2 px-3 py-1.5 shrink-0" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
                <PersonaAvatar name={selectedPersona} size={18} />
                <span className="text-xs font-medium flex-1" style={{ color: 'var(--text-primary)' }}>
                  {selectedPersona[0].toUpperCase() + selectedPersona.slice(1)} — Terminal
                </span>
                {personas.find(p => p.name === selectedPersona)?.context_pct != null && (
                  <ContextBar pct={personas.find(p => p.name === selectedPersona)!.context_pct!} />
                )}
              </div>
              <div className="flex-1 overflow-hidden">
                <Suspense fallback={<div className="p-4 text-xs" style={{ color: 'var(--text-muted)' }}>Loading terminal...</div>}>
                  <TerminalTile
                    key={selectedPersona}
                    persona={selectedPersona}
                    tmuxPane={personas.find(p => p.name === selectedPersona)?.tmux_pane}
                    awake={personas.find(p => p.name === selectedPersona)?.awake}
                    hideHeader
                  />
                </Suspense>
              </div>
            </div>

            {/* Split terminal (Shift+Click) */}
            {splitPersona && (
              <>
                {/* Draggable resize handle */}
                <div
                  style={{ width: 5, cursor: 'col-resize', flexShrink: 0, background: 'transparent' }}
                  onMouseEnter={e => { (e.target as HTMLElement).style.background = 'var(--accent)' }}
                  onMouseLeave={e => { (e.target as HTMLElement).style.background = 'transparent' }}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    const area = termAreaRef.current
                    if (!area) return
                    const areaRect = area.getBoundingClientRect()
                    document.body.style.cursor = 'col-resize'
                    document.body.style.userSelect = 'none'
                    const onMove = (ev: MouseEvent) => {
                      const ratio = Math.max(0.2, Math.min(0.8, (ev.clientX - areaRect.left) / areaRect.width))
                      setSplitRatio(ratio)
                    }
                    const onUp = () => {
                      document.body.style.cursor = ''
                      document.body.style.userSelect = ''
                      document.removeEventListener('mousemove', onMove)
                      document.removeEventListener('mouseup', onUp)
                      // Trigger resize event so xterm.js re-fits
                      window.dispatchEvent(new Event('resize'))
                    }
                    document.addEventListener('mousemove', onMove)
                    document.addEventListener('mouseup', onUp)
                  }}
                />
                <div className="flex flex-col overflow-hidden min-w-0" style={{ width: `${(1 - splitRatio) * 100}%` }}>
                  <div className="flex items-center gap-2 px-3 py-1.5 shrink-0" style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
                    <PersonaAvatar name={splitPersona} size={18} />
                    <span className="text-xs font-medium flex-1" style={{ color: 'var(--text-primary)' }}>
                      {splitPersona[0].toUpperCase() + splitPersona.slice(1)} — Terminal
                    </span>
                    {personas.find(p => p.name === splitPersona)?.context_pct != null && (
                      <ContextBar pct={personas.find(p => p.name === splitPersona)!.context_pct!} />
                    )}
                    <button
                      onClick={() => { setSplitPersona(null); setSplitRatio(0.5) }}
                      className="text-xs px-1.5 py-0.5 rounded"
                      style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: 11 }}
                      title="Close split"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <Suspense fallback={<div className="p-4 text-xs" style={{ color: 'var(--text-muted)' }}>Loading terminal...</div>}>
                      <TerminalTile
                        key={splitPersona}
                        persona={splitPersona}
                        tmuxPane={personas.find(p => p.name === splitPersona)?.tmux_pane}
                        awake={personas.find(p => p.name === splitPersona)?.awake}
                        hideHeader
                      />
                    </Suspense>
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center h-full w-full">
            <div className="text-center">
              <div className="text-2xl mb-2" style={{ opacity: 0.15 }}>⌨</div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Select a persona to open their terminal
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function WalletsTab() {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['wallets'],
    queryFn: async () => {
      const res = await fetch('/api/wallets')
      if (!res.ok) throw new Error(`${res.status}`)
      return res.json()
    },
    staleTime: 30_000,
  })

  const wallets = data?.wallets || []
  const funded = wallets.filter((w: any) => w.has_funds)
  const totalSol = data?.total_sol || 0
  const totalUsdc = data?.total_usdc || 0

  if (isLoading) return <div className="text-xs" style={{ color: 'var(--text-muted)', padding: 20 }}>Loading wallets...</div>

  return (
    <div className="mb-6">
      {/* Summary bar */}
      <div className="flex gap-4 mb-4 p-3 rounded-lg" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}>
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Total Wallets:</span>
          <span className="text-sm font-bold">{wallets.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Funded:</span>
          <span className="text-sm font-bold" style={{ color: '#5AC884' }}>{funded.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>SOL:</span>
          <span className="text-sm font-bold" style={{ color: '#9945FF' }}>{totalSol.toFixed(4)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>USDC:</span>
          <span className="text-sm font-bold" style={{ color: '#2775CA' }}>${totalUsdc.toFixed(2)}</span>
        </div>
      </div>

      {/* Wallet cards */}
      <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
        {wallets.map((w: any) => {
          const sol = w.sol_balance || 0
          const usdc = w.usdc_balance || 0
          const tokens = w.other_tokens || []
          return (
            <div
              key={w.persona || w.address}
              className="rounded-lg p-3"
              style={{
                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                borderLeft: `3px solid ${w.has_funds ? '#9945FF' : 'var(--border)'}`,
                opacity: w.has_funds ? 1 : 0.6,
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <PersonaAvatar name={w.persona || '?'} size={22} />
                <span className="text-sm font-semibold" style={{ textTransform: 'capitalize' }}>
                  {w.persona || 'Unknown'}
                </span>
                {w.has_funds && (
                  <span className="text-xs px-1.5 py-0.5 rounded" style={{
                    background: 'rgba(153,69,255,0.1)', color: '#9945FF', fontWeight: 600,
                  }}>Funded</span>
                )}
              </div>
              <div className="text-xs mb-2" style={{ color: 'var(--text-muted)', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {w.address ? `${w.address.slice(0, 8)}...${w.address.slice(-6)}` : '—'}
              </div>
              <div className="flex gap-3 text-xs">
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>SOL </span>
                  <span className="font-semibold" style={{ color: sol > 0 ? '#9945FF' : 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    {sol.toFixed(4)}
                  </span>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>USDC </span>
                  <span className="font-semibold" style={{ color: usdc > 0 ? '#2775CA' : 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    ${usdc.toFixed(2)}
                  </span>
                </div>
                {tokens.length > 0 && tokens.map((t: any) => (
                  <div key={t.mint || t.symbol}>
                    <span style={{ color: 'var(--text-muted)' }}>{t.symbol || '?'} </span>
                    <span className="font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {t.amount?.toFixed(2) || '0'}
                    </span>
                  </div>
                ))}
              </div>
              {w.explorer_url && (
                <a
                  href={w.explorer_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs mt-2 inline-block"
                  style={{ color: '#856FC8', textDecoration: 'none' }}
                >
                  View on Solscan →
                </a>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
